import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ERROR_CODES, ErrorCode, getErrorMessage } from './errorMatcher'
import PlatformError from './PlatformError'

export interface FetchState<T> {
  data: T | null
  loading: boolean
  error: PlatformError | null
  callFetch: () => void
  headers: Response['headers'] | null
}

/**
 * useFetch is a custom hook to fetch data from a given URL. It returns stateful values which
 * can be used to render ui components through the life cycle of the fetch call.
 * @param url - The URL to fetch data from.
 * @param requestFields - Optional request fields for the fetch call.
 *
 * @returns An object containing:
 * - `data`: A stateful value containing the fetched data.
 * - `loading`: A stateful value containing a boolean indicating whether the fetch is in progress.
 * - `error`: A stateful value containing an error if one occurred.
 * - `callFetch`: A function that triggers the fetch operation and returns nothing. Outputs of the fetch are stored in
 *       the stateful values.
 */
const useFetch = <T>(url: string, requestFields?: RequestInit): FetchState<T> => {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<PlatformError | null>(null)
  const [headers, setHeaders] = useState<Response['headers'] | null>(null)
  const [shouldCallFetch, setShouldCallFetch] = useState<boolean>(false)
  const { t } = useTranslation('vpc')

  const callFetch = useCallback(() => {
    if (!shouldCallFetch) {
      setShouldCallFetch(true)
    }
  }, [shouldCallFetch])

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)
      setShouldCallFetch(false)
      try {
        const response = await fetch(url, requestFields)
        setHeaders(response.headers)

        const text = await response.text()
        const result = text ? JSON.parse(text) : {}

        if (!response.ok) {
          // Get the error code from the response body, fallback to unknown if not provided
          const errorCode = result.code || ERROR_CODES.ERR_UNKNOWN
          const errorMessage = getErrorMessage(errorCode as ErrorCode, t)
          throw new PlatformError(errorCode as ErrorCode, errorMessage, result.details || {})
        }

        setError(null)
        setData(result as T)
      } catch (error) {
        setError(error as PlatformError)
      } finally {
        setLoading(false)
      }
    }

    if (shouldCallFetch && url && !loading) {
      fetchData()
    }
  }, [url, requestFields, shouldCallFetch, loading, t])

  return { data, loading, error, callFetch, headers }
}

export default useFetch
